import type { Criticality, Disposition, Lifecycle, TechnicalHealth } from '@aurora-pvi/contracts';
import type { Rng } from './rng';
import { idFor } from './ids';
import { SOURCE_SYSTEMS, provenanceFactory } from './provenance';
import type { Application } from './types';
import { addDays, at, clamp, dollars, millions, pad, round } from './support';

/**
 * The application estate: 40 curated systems plus a generated tail to 1,900.
 *
 * The tail is not padding. Virtualised grids, facet counts and the performance
 * budget are all claims that only mean something at estate scale, and an
 * inventory of forty rows lets every one of them go untested.
 */

export const DEFAULT_APPLICATION_COUNT = 1900;

/** Bands, so a health chip and its score can never contradict each other. */
export function technicalHealthFromScore(score: number): TechnicalHealth {
  if (score >= 75) return 'Good';
  if (score >= 60) return 'Degraded';
  if (score >= 45) return 'AtRisk';
  return 'Poor';
}

const DOMAINS = [
  'Flight Ops',
  'Crew',
  'Airport Ops',
  'Maintenance',
  'Reservations',
  'Digital',
  'Loyalty',
  'Commercial',
  'Cargo',
  'Finance',
  'Corporate',
  'Data & Analytics',
  'Network Ops',
  'Security',
];

const CAPABILITY_BY_DOMAIN: Record<string, readonly string[]> = {
  'Flight Ops': ['Flight Planning', 'Dispatch', 'Weight & Balance', 'Datalink', 'Flight Following'],
  Crew: [
    'Crew Planning',
    'Crew Tracking',
    'Crew Communications',
    'Crew Qualifications',
    'Crew Pay',
  ],
  'Airport Ops': [
    'Departure Control',
    'Baggage Handling',
    'Resource Allocation',
    'Boarding',
    'Ground Handling',
  ],
  Maintenance: [
    'Maintenance Planning',
    'Technical Records',
    'Parts Inventory',
    'Defect Management',
  ],
  Reservations: ['Bookings', 'Ticketing', 'Inventory', 'Offer Management', 'Order Management'],
  Digital: ['Digital Experience', 'Mobile', 'Content Management', 'Search'],
  Loyalty: ['Loyalty Management', 'Partner Settlement', 'Member Communications', 'Redemption'],
  Commercial: ['Pricing', 'Revenue Management', 'Ancillary Retailing', 'Sales Reporting'],
  Cargo: ['Cargo Booking', 'Cargo Capacity', 'Cargo Settlement', 'Dangerous Goods'],
  Finance: ['Revenue Accounting', 'Payments', 'General Ledger', 'Procure to Pay', 'Treasury'],
  Corporate: ['HR', 'Learning', 'Expense Management', 'Workplace', 'Legal'],
  'Data & Analytics': [
    'Data Platform',
    'Business Intelligence',
    'Machine Learning',
    'Data Quality',
  ],
  'Network Ops': ['Network Operations', 'Schedule Management', 'Slot Management'],
  Security: ['Identity & Access', 'Threat Detection', 'Key Management'],
};

const HOSTING = [
  'Aurora Private Cloud',
  'Public Cloud (Region A)',
  'Public Cloud (Region B)',
  'On-premise DC-North',
  'On-premise DC-South',
  'SaaS (vendor hosted)',
];
const ARCHITECTURES = [
  'Monolith',
  'Modular Monolith',
  'Microservices',
  'Client/Server',
  'Mainframe',
  'Serverless',
  'Packaged SaaS',
];
const STACKS = [
  'Java / Oracle',
  'Java / PostgreSQL',
  '.NET / SQL Server',
  'Node.js / PostgreSQL',
  'Python / Columnar DW',
  'COBOL / DB2',
  'Go / Kafka',
  'Vendor proprietary',
];
const DATABASES = [
  'Oracle 19c',
  'PostgreSQL 15',
  'SQL Server 2019',
  'DB2 z/OS',
  'MongoDB 6',
  'Aurora Data Hub (managed)',
  'Vendor managed',
];

interface CuratedAppSeed {
  code: string;
  name: string;
  domain: string;
  capability: string;
  criticality: Criticality;
  technicalHealthScore: number;
  annualRunCostMillions: number;
  architecture: string;
  stack: string;
  database: string;
  hosting: string;
  dataSensitivity: Application['dataSensitivity'];
  lifecycleSince: string;
  version: string;
  vendorCode: string | null;
  integrationCount: number;
  duplicateOfCodes?: string[];
  disposition?: Disposition;
  lifecycle?: Lifecycle;
}

const CURATED_APPS: readonly CuratedAppSeed[] = [
  {
    code: 'APP-0001',
    name: 'Aurora PSS Core',
    domain: 'Reservations',
    capability: 'Bookings',
    criticality: 'MissionCritical',
    technicalHealthScore: 62,
    annualRunCostMillions: 31.5,
    architecture: 'Mainframe',
    stack: 'COBOL / DB2',
    database: 'DB2 z/OS',
    hosting: 'On-premise DC-North',
    dataSensitivity: 'High',
    lifecycleSince: '2001-04-02',
    version: '18.6',
    vendorCode: 'SUP-MERIDIAN',
    integrationCount: 128,
    disposition: 'Modernize',
  },
  {
    code: 'APP-0002',
    name: 'SkyRes Shopping Engine',
    domain: 'Reservations',
    capability: 'Offer Management',
    criticality: 'MissionCritical',
    technicalHealthScore: 84,
    annualRunCostMillions: 22.3,
    architecture: 'Microservices',
    stack: 'Go / Kafka',
    database: 'PostgreSQL 15',
    hosting: 'Public Cloud (Region A)',
    dataSensitivity: 'High',
    lifecycleSince: '2019-02-11',
    version: '7.4',
    vendorCode: null,
    integrationCount: 46,
    disposition: 'Retain',
  },
  {
    code: 'APP-0003',
    name: 'Aurora.com Digital Platform',
    domain: 'Digital',
    capability: 'Digital Experience',
    criticality: 'MissionCritical',
    technicalHealthScore: 81,
    annualRunCostMillions: 18.7,
    architecture: 'Microservices',
    stack: 'Node.js / PostgreSQL',
    database: 'PostgreSQL 15',
    hosting: 'Public Cloud (Region A)',
    dataSensitivity: 'High',
    lifecycleSince: '2018-06-25',
    version: '11.2',
    vendorCode: null,
    integrationCount: 62,
    disposition: 'Retain',
  },
  {
    code: 'APP-0004',
    name: 'Aurora Mobile Platform',
    domain: 'Digital',
    capability: 'Mobile',
    criticality: 'MissionCritical',
    technicalHealthScore: 79,
    annualRunCostMillions: 12.4,
    architecture: 'Microservices',
    stack: 'Node.js / PostgreSQL',
    database: 'PostgreSQL 15',
    hosting: 'Public Cloud (Region A)',
    dataSensitivity: 'High',
    lifecycleSince: '2019-09-03',
    version: '9.8',
    vendorCode: null,
    integrationCount: 41,
    disposition: 'Retain',
  },
  {
    code: 'APP-0005',
    name: 'SkyPort Departure Control',
    domain: 'Airport Ops',
    capability: 'Departure Control',
    criticality: 'MissionCritical',
    technicalHealthScore: 58,
    annualRunCostMillions: 24.8,
    architecture: 'Client/Server',
    stack: 'Java / Oracle',
    database: 'Oracle 19c',
    hosting: 'On-premise DC-North',
    dataSensitivity: 'High',
    lifecycleSince: '2009-03-15',
    version: '10.4',
    vendorCode: 'SUP-MERIDIAN',
    integrationCount: 34,
    disposition: 'Modernize',
  },
  {
    code: 'APP-0006',
    name: 'SelfServe Kiosk Platform',
    domain: 'Airport Ops',
    capability: 'Departure Control',
    criticality: 'BusinessCritical',
    technicalHealthScore: 66,
    annualRunCostMillions: 6.4,
    architecture: 'Client/Server',
    stack: '.NET / SQL Server',
    database: 'SQL Server 2019',
    hosting: 'On-premise DC-South',
    dataSensitivity: 'Medium',
    lifecycleSince: '2014-05-20',
    version: '5.1',
    vendorCode: 'SUP-CALDER',
    integrationCount: 18,
    duplicateOfCodes: ['APP-0005'],
    disposition: 'Consolidate',
  },
  {
    code: 'APP-0007',
    name: 'BagTrack Reconciliation',
    domain: 'Airport Ops',
    capability: 'Baggage Handling',
    criticality: 'BusinessCritical',
    technicalHealthScore: 51,
    annualRunCostMillions: 9.6,
    architecture: 'Monolith',
    stack: 'Java / Oracle',
    database: 'Oracle 19c',
    hosting: 'On-premise DC-South',
    dataSensitivity: 'Medium',
    lifecycleSince: '2007-11-08',
    version: '4.9',
    vendorCode: 'SUP-VANTAGE',
    integrationCount: 27,
    disposition: 'Replace',
  },
  {
    code: 'APP-0008',
    name: 'BagFlow Sortation Interface',
    domain: 'Airport Ops',
    capability: 'Baggage Handling',
    criticality: 'BusinessCritical',
    technicalHealthScore: 63,
    annualRunCostMillions: 4.2,
    architecture: 'Modular Monolith',
    stack: 'Java / PostgreSQL',
    database: 'PostgreSQL 15',
    hosting: 'On-premise DC-South',
    dataSensitivity: 'Low',
    lifecycleSince: '2016-01-19',
    version: '3.3',
    vendorCode: 'SUP-VANTAGE',
    integrationCount: 12,
    duplicateOfCodes: ['APP-0007'],
    disposition: 'Consolidate',
  },
  {
    code: 'APP-0009',
    name: 'TurnBoard Ops Console',
    domain: 'Airport Ops',
    capability: 'Resource Allocation',
    criticality: 'BusinessCritical',
    technicalHealthScore: 71,
    annualRunCostMillions: 5.9,
    architecture: 'Microservices',
    stack: 'Node.js / PostgreSQL',
    database: 'PostgreSQL 15',
    hosting: 'Aurora Private Cloud',
    dataSensitivity: 'Low',
    lifecycleSince: '2018-11-05',
    version: '6.2',
    vendorCode: null,
    integrationCount: 23,
    disposition: 'Retain',
  },
  {
    code: 'APP-0010',
    name: 'CrewPair Optimizer',
    domain: 'Crew',
    capability: 'Crew Planning',
    criticality: 'MissionCritical',
    technicalHealthScore: 44,
    annualRunCostMillions: 14.2,
    architecture: 'Monolith',
    stack: 'Java / Oracle',
    database: 'Oracle 19c',
    hosting: 'On-premise DC-North',
    dataSensitivity: 'High',
    lifecycleSince: '2005-06-01',
    version: '12.1',
    vendorCode: 'SUP-MERIDIAN',
    integrationCount: 31,
    disposition: 'Modernize',
  },
  {
    code: 'APP-0011',
    name: 'CrewRoster Manager',
    domain: 'Crew',
    capability: 'Crew Planning',
    criticality: 'MissionCritical',
    technicalHealthScore: 47,
    annualRunCostMillions: 11.8,
    architecture: 'Monolith',
    stack: 'Java / Oracle',
    database: 'Oracle 19c',
    hosting: 'On-premise DC-North',
    dataSensitivity: 'High',
    lifecycleSince: '2005-06-01',
    version: '12.1',
    vendorCode: 'SUP-MERIDIAN',
    integrationCount: 29,
    duplicateOfCodes: ['APP-0010'],
    disposition: 'Consolidate',
  },
  {
    code: 'APP-0012',
    name: 'CrewLink Mobile',
    domain: 'Crew',
    capability: 'Crew Communications',
    criticality: 'BusinessCritical',
    technicalHealthScore: 83,
    annualRunCostMillions: 4.6,
    architecture: 'Microservices',
    stack: 'Node.js / PostgreSQL',
    database: 'PostgreSQL 15',
    hosting: 'Public Cloud (Region A)',
    dataSensitivity: 'Medium',
    lifecycleSince: '2020-04-14',
    version: '4.7',
    vendorCode: null,
    integrationCount: 16,
    disposition: 'Retain',
  },
  {
    code: 'APP-0013',
    name: 'CrewTrack Day-of-Ops',
    domain: 'Crew',
    capability: 'Crew Tracking',
    criticality: 'MissionCritical',
    technicalHealthScore: 86,
    annualRunCostMillions: 8.9,
    architecture: 'Microservices',
    stack: 'Go / Kafka',
    database: 'PostgreSQL 15',
    hosting: 'Aurora Private Cloud',
    dataSensitivity: 'High',
    lifecycleSince: '2019-03-15',
    version: '8.3',
    vendorCode: null,
    integrationCount: 38,
    disposition: 'Retain',
  },
  {
    code: 'APP-0014',
    name: 'FatigueGuard Compliance Engine',
    domain: 'Crew',
    capability: 'Crew Qualifications',
    criticality: 'MissionCritical',
    technicalHealthScore: 74,
    annualRunCostMillions: 3.8,
    architecture: 'Modular Monolith',
    stack: 'Java / PostgreSQL',
    database: 'PostgreSQL 15',
    hosting: 'Aurora Private Cloud',
    dataSensitivity: 'High',
    lifecycleSince: '2021-02-08',
    version: '3.1',
    vendorCode: 'SUP-NORTHWIND',
    integrationCount: 14,
    disposition: 'Retain',
  },
  {
    code: 'APP-0015',
    name: 'FlightPlan Pro',
    domain: 'Flight Ops',
    capability: 'Flight Planning',
    criticality: 'MissionCritical',
    technicalHealthScore: 77,
    annualRunCostMillions: 13.4,
    architecture: 'Packaged SaaS',
    stack: 'Vendor proprietary',
    database: 'Vendor managed',
    hosting: 'SaaS (vendor hosted)',
    dataSensitivity: 'High',
    lifecycleSince: '2015-08-30',
    version: '22.4',
    vendorCode: 'SUP-NORTHWIND',
    integrationCount: 44,
    disposition: 'Retain',
  },
  {
    code: 'APP-0016',
    name: 'Datalink Gateway',
    domain: 'Flight Ops',
    capability: 'Datalink',
    criticality: 'MissionCritical',
    technicalHealthScore: 55,
    annualRunCostMillions: 7.1,
    architecture: 'Client/Server',
    stack: 'Java / Oracle',
    database: 'Oracle 19c',
    hosting: 'On-premise DC-North',
    dataSensitivity: 'High',
    lifecycleSince: '2004-02-17',
    version: '9.0',
    vendorCode: 'SUP-CALDER',
    integrationCount: 22,
    disposition: 'Modernize',
  },
  {
    code: 'APP-0017',
    name: 'LoadBalance Engine',
    domain: 'Flight Ops',
    capability: 'Weight & Balance',
    criticality: 'MissionCritical',
    technicalHealthScore: 72,
    annualRunCostMillions: 5.2,
    architecture: 'Modular Monolith',
    stack: 'Java / PostgreSQL',
    database: 'PostgreSQL 15',
    hosting: 'Aurora Private Cloud',
    dataSensitivity: 'High',
    lifecycleSince: '2013-10-01',
    version: '6.8',
    vendorCode: null,
    integrationCount: 19,
    disposition: 'Retain',
  },
  {
    code: 'APP-0018',
    name: 'OpsControl Hub',
    domain: 'Network Ops',
    capability: 'Network Operations',
    criticality: 'MissionCritical',
    technicalHealthScore: 49,
    annualRunCostMillions: 12.1,
    architecture: 'Monolith',
    stack: 'Java / Oracle',
    database: 'Oracle 19c',
    hosting: 'On-premise DC-North',
    dataSensitivity: 'High',
    lifecycleSince: '2006-09-11',
    version: '14.2',
    vendorCode: 'SUP-MERIDIAN',
    integrationCount: 57,
    disposition: 'Modernize',
  },
  {
    code: 'APP-0019',
    name: 'FuelWise Analytics',
    domain: 'Flight Ops',
    capability: 'Flight Following',
    criticality: 'Important',
    technicalHealthScore: 88,
    annualRunCostMillions: 3.1,
    architecture: 'Serverless',
    stack: 'Python / Columnar DW',
    database: 'Aurora Data Hub (managed)',
    hosting: 'Public Cloud (Region B)',
    dataSensitivity: 'Low',
    lifecycleSince: '2021-09-06',
    version: '2.9',
    vendorCode: null,
    integrationCount: 11,
    disposition: 'Retain',
  },
  {
    code: 'APP-0020',
    name: 'MaintenanceCore MRO',
    domain: 'Maintenance',
    capability: 'Maintenance Planning',
    criticality: 'BusinessCritical',
    technicalHealthScore: 53,
    annualRunCostMillions: 10.6,
    architecture: 'Monolith',
    stack: 'Java / Oracle',
    database: 'Oracle 19c',
    hosting: 'On-premise DC-South',
    dataSensitivity: 'Medium',
    lifecycleSince: '2008-05-22',
    version: '11.7',
    vendorCode: 'SUP-VANTAGE',
    integrationCount: 33,
    disposition: 'Modernize',
  },
  {
    code: 'APP-0021',
    name: 'TechLog Mobile',
    domain: 'Maintenance',
    capability: 'Technical Records',
    criticality: 'BusinessCritical',
    technicalHealthScore: 76,
    annualRunCostMillions: 2.8,
    architecture: 'Microservices',
    stack: 'Node.js / PostgreSQL',
    database: 'PostgreSQL 15',
    hosting: 'Public Cloud (Region A)',
    dataSensitivity: 'Medium',
    lifecycleSince: '2020-11-12',
    version: '3.4',
    vendorCode: null,
    integrationCount: 9,
    disposition: 'Retain',
  },
  {
    code: 'APP-0022',
    name: 'NetPlan Scheduler',
    domain: 'Network Ops',
    capability: 'Schedule Management',
    criticality: 'BusinessCritical',
    technicalHealthScore: 68,
    annualRunCostMillions: 8.2,
    architecture: 'Packaged SaaS',
    stack: 'Vendor proprietary',
    database: 'Vendor managed',
    hosting: 'SaaS (vendor hosted)',
    dataSensitivity: 'Medium',
    lifecycleSince: '2013-03-04',
    version: '17.1',
    vendorCode: 'SUP-NORTHWIND',
    integrationCount: 26,
    disposition: 'Retain',
  },
  {
    code: 'APP-0023',
    name: 'SlotDesk Manager',
    domain: 'Network Ops',
    capability: 'Slot Management',
    criticality: 'Important',
    technicalHealthScore: 59,
    annualRunCostMillions: 1.9,
    architecture: 'Client/Server',
    stack: '.NET / SQL Server',
    database: 'SQL Server 2019',
    hosting: 'On-premise DC-South',
    dataSensitivity: 'Low',
    lifecycleSince: '2010-07-19',
    version: '4.2',
    vendorCode: 'SUP-CALDER',
    integrationCount: 7,
    disposition: 'Investigate',
  },
  {
    code: 'APP-0024',
    name: 'CargoBook Portal',
    domain: 'Cargo',
    capability: 'Cargo Booking',
    criticality: 'BusinessCritical',
    technicalHealthScore: 64,
    annualRunCostMillions: 6.8,
    architecture: 'Modular Monolith',
    stack: 'Java / PostgreSQL',
    database: 'PostgreSQL 15',
    hosting: 'Aurora Private Cloud',
    dataSensitivity: 'Medium',
    lifecycleSince: '2016-11-30',
    version: '5.6',
    vendorCode: null,
    integrationCount: 21,
    disposition: 'Retain',
  },
  {
    code: 'APP-0025',
    name: 'ULD & Cargo Weight Manager',
    domain: 'Cargo',
    capability: 'Cargo Capacity',
    criticality: 'Important',
    technicalHealthScore: 57,
    annualRunCostMillions: 2.2,
    architecture: 'Client/Server',
    stack: 'Java / Oracle',
    database: 'Oracle 19c',
    hosting: 'On-premise DC-South',
    dataSensitivity: 'Low',
    lifecycleSince: '2011-04-06',
    version: '3.8',
    vendorCode: 'SUP-VANTAGE',
    integrationCount: 8,
    disposition: 'Investigate',
  },
  {
    code: 'APP-0026',
    name: 'Skyward Loyalty Engine',
    domain: 'Loyalty',
    capability: 'Loyalty Management',
    criticality: 'MissionCritical',
    technicalHealthScore: 80,
    annualRunCostMillions: 14.2,
    architecture: 'Modular Monolith',
    stack: 'Java / PostgreSQL',
    database: 'PostgreSQL 15',
    hosting: 'Aurora Private Cloud',
    dataSensitivity: 'High',
    lifecycleSince: '2015-06-29',
    version: '9.5',
    vendorCode: null,
    integrationCount: 52,
    disposition: 'Retain',
  },
  {
    code: 'APP-0027',
    name: 'Partner Settlement Hub',
    domain: 'Loyalty',
    capability: 'Partner Settlement',
    criticality: 'BusinessCritical',
    technicalHealthScore: 38,
    annualRunCostMillions: 4.9,
    architecture: 'Monolith',
    stack: 'COBOL / DB2',
    database: 'DB2 z/OS',
    hosting: 'On-premise DC-North',
    dataSensitivity: 'High',
    lifecycleSince: '2003-02-12',
    version: '7.0',
    vendorCode: 'SUP-MERIDIAN',
    integrationCount: 17,
    disposition: 'Replace',
  },
  {
    code: 'APP-0028',
    name: 'Aurora CRM',
    domain: 'Digital',
    capability: 'Digital Experience',
    criticality: 'BusinessCritical',
    technicalHealthScore: 70,
    annualRunCostMillions: 11.3,
    architecture: 'Packaged SaaS',
    stack: 'Vendor proprietary',
    database: 'Vendor managed',
    hosting: 'SaaS (vendor hosted)',
    dataSensitivity: 'High',
    lifecycleSince: '2017-02-20',
    version: '25.1',
    vendorCode: 'SUP-BRIGHTPATH',
    integrationCount: 48,
    disposition: 'Retain',
  },
  {
    code: 'APP-0029',
    name: 'NotifyHub',
    domain: 'Digital',
    capability: 'Content Management',
    criticality: 'BusinessCritical',
    technicalHealthScore: 67,
    annualRunCostMillions: 5.4,
    architecture: 'Serverless',
    stack: 'Go / Kafka',
    database: 'PostgreSQL 15',
    hosting: 'Public Cloud (Region A)',
    dataSensitivity: 'Medium',
    lifecycleSince: '2018-01-08',
    version: '6.9',
    vendorCode: null,
    integrationCount: 39,
    disposition: 'Retain',
  },
  {
    code: 'APP-0030',
    name: 'CareDesk Contact Suite',
    domain: 'Digital',
    capability: 'Digital Experience',
    criticality: 'BusinessCritical',
    technicalHealthScore: 61,
    annualRunCostMillions: 9.1,
    architecture: 'Packaged SaaS',
    stack: 'Vendor proprietary',
    database: 'Vendor managed',
    hosting: 'SaaS (vendor hosted)',
    dataSensitivity: 'High',
    lifecycleSince: '2016-08-15',
    version: '19.3',
    vendorCode: 'SUP-BRIGHTPATH',
    integrationCount: 31,
    duplicateOfCodes: ['APP-0028'],
    disposition: 'Consolidate',
  },
  {
    code: 'APP-0031',
    name: 'PayGate',
    domain: 'Finance',
    capability: 'Payments',
    criticality: 'MissionCritical',
    technicalHealthScore: 82,
    annualRunCostMillions: 10.8,
    architecture: 'Microservices',
    stack: 'Java / PostgreSQL',
    database: 'PostgreSQL 15',
    hosting: 'Aurora Private Cloud',
    dataSensitivity: 'High',
    lifecycleSince: '2017-05-16',
    version: '8.1',
    vendorCode: null,
    integrationCount: 43,
    disposition: 'Retain',
  },
  {
    code: 'APP-0032',
    name: 'FraudShield Screening',
    domain: 'Security',
    capability: 'Threat Detection',
    criticality: 'BusinessCritical',
    technicalHealthScore: 78,
    annualRunCostMillions: 3.6,
    architecture: 'Packaged SaaS',
    stack: 'Vendor proprietary',
    database: 'Vendor managed',
    hosting: 'SaaS (vendor hosted)',
    dataSensitivity: 'High',
    lifecycleSince: '2019-06-11',
    version: '12.7',
    vendorCode: 'SUP-BRIGHTPATH',
    integrationCount: 13,
    disposition: 'Retain',
  },
  {
    code: 'APP-0033',
    name: 'RevAcct Ledger',
    domain: 'Finance',
    capability: 'Revenue Accounting',
    criticality: 'MissionCritical',
    technicalHealthScore: 36,
    annualRunCostMillions: 16.4,
    architecture: 'Mainframe',
    stack: 'COBOL / DB2',
    database: 'DB2 z/OS',
    hosting: 'On-premise DC-North',
    dataSensitivity: 'High',
    lifecycleSince: '1998-04-03',
    version: '22.0',
    vendorCode: 'SUP-MERIDIAN',
    integrationCount: 61,
    disposition: 'Replace',
  },
  {
    code: 'APP-0034',
    name: 'ProrateEngine',
    domain: 'Finance',
    capability: 'Revenue Accounting',
    criticality: 'BusinessCritical',
    technicalHealthScore: 41,
    annualRunCostMillions: 5.7,
    architecture: 'Monolith',
    stack: 'COBOL / DB2',
    database: 'DB2 z/OS',
    hosting: 'On-premise DC-North',
    dataSensitivity: 'High',
    lifecycleSince: '2000-10-16',
    version: '15.4',
    vendorCode: 'SUP-MERIDIAN',
    integrationCount: 24,
    duplicateOfCodes: ['APP-0033'],
    disposition: 'Consolidate',
  },
  {
    code: 'APP-0035',
    name: 'PriceOptimizer',
    domain: 'Commercial',
    capability: 'Revenue Management',
    criticality: 'MissionCritical',
    technicalHealthScore: 75,
    annualRunCostMillions: 17.9,
    architecture: 'Packaged SaaS',
    stack: 'Vendor proprietary',
    database: 'Vendor managed',
    hosting: 'SaaS (vendor hosted)',
    dataSensitivity: 'High',
    lifecycleSince: '2014-09-15',
    version: '31.2',
    vendorCode: 'SUP-NORTHWIND',
    integrationCount: 37,
    disposition: 'Retain',
  },
  {
    code: 'APP-0036',
    name: 'AncillaryOffer Engine',
    domain: 'Commercial',
    capability: 'Ancillary Retailing',
    criticality: 'BusinessCritical',
    technicalHealthScore: 65,
    annualRunCostMillions: 7.3,
    architecture: 'Microservices',
    stack: 'Node.js / PostgreSQL',
    database: 'PostgreSQL 15',
    hosting: 'Public Cloud (Region A)',
    dataSensitivity: 'Medium',
    lifecycleSince: '2019-07-22',
    version: '5.2',
    vendorCode: null,
    integrationCount: 28,
    disposition: 'Retain',
  },
  {
    code: 'APP-0037',
    name: 'Aurora Data Hub',
    domain: 'Data & Analytics',
    capability: 'Data Platform',
    criticality: 'MissionCritical',
    technicalHealthScore: 85,
    annualRunCostMillions: 21.6,
    architecture: 'Serverless',
    stack: 'Python / Columnar DW',
    database: 'Aurora Data Hub (managed)',
    hosting: 'Public Cloud (Region B)',
    dataSensitivity: 'High',
    lifecycleSince: '2020-01-27',
    version: '4.8',
    vendorCode: null,
    integrationCount: 214,
    disposition: 'Retain',
  },
  {
    code: 'APP-0038',
    name: 'InsightWorks BI',
    domain: 'Data & Analytics',
    capability: 'Business Intelligence',
    criticality: 'Important',
    technicalHealthScore: 73,
    annualRunCostMillions: 6.2,
    architecture: 'Packaged SaaS',
    stack: 'Vendor proprietary',
    database: 'Vendor managed',
    hosting: 'SaaS (vendor hosted)',
    dataSensitivity: 'Medium',
    lifecycleSince: '2018-03-19',
    version: '14.6',
    vendorCode: 'SUP-BRIGHTPATH',
    integrationCount: 87,
    disposition: 'Retain',
  },
  {
    code: 'APP-0039',
    name: 'IdentityBridge',
    domain: 'Security',
    capability: 'Identity & Access',
    criticality: 'MissionCritical',
    technicalHealthScore: 87,
    annualRunCostMillions: 8.4,
    architecture: 'Packaged SaaS',
    stack: 'Vendor proprietary',
    database: 'Vendor managed',
    hosting: 'SaaS (vendor hosted)',
    dataSensitivity: 'High',
    lifecycleSince: '2019-10-08',
    version: '9.9',
    vendorCode: 'SUP-BRIGHTPATH',
    integrationCount: 176,
    disposition: 'Retain',
  },
  {
    code: 'APP-0040',
    name: 'IntegrationMesh',
    domain: 'Data & Analytics',
    capability: 'Data Platform',
    criticality: 'MissionCritical',
    technicalHealthScore: 69,
    annualRunCostMillions: 12.9,
    architecture: 'Microservices',
    stack: 'Java / PostgreSQL',
    database: 'PostgreSQL 15',
    hosting: 'Aurora Private Cloud',
    dataSensitivity: 'High',
    lifecycleSince: '2017-11-14',
    version: '7.7',
    vendorCode: null,
    integrationCount: 305,
    disposition: 'Modernize',
  },
];

/** Name fragments for the generated tail. */
const PREFIXES = [
  'Aero',
  'Sky',
  'Aurora',
  'North',
  'Flight',
  'Trip',
  'Gate',
  'Ramp',
  'Cargo',
  'Fare',
  'Crew',
  'Bag',
  'Ops',
  'Data',
  'Cabin',
  'Route',
  'Slot',
  'Port',
  'Deck',
  'Wing',
];
const SUFFIXES = [
  'Desk',
  'Hub',
  'Works',
  'Track',
  'Link',
  'Sync',
  'View',
  'Bridge',
  'Vault',
  'Console',
  'Registry',
  'Manager',
  'Gateway',
  'Studio',
  'Monitor',
  'Insight',
  'Suite',
  'Engine',
  'Service',
  'Portal',
];

const LIFECYCLE_MIX: readonly (readonly [Lifecycle, number])[] = [
  ['Active', 84],
  ['Candidate', 6],
  ['Sunset', 6],
  ['Retired', 3],
  ['Superseded', 1],
];

const LIFECYCLE_LABELS: Record<Lifecycle, string> = {
  Candidate: 'Candidate',
  Proposed: 'Proposed',
  Active: 'In Production',
  Sunset: 'Sunsetting',
  Retired: 'Retired',
  Superseded: 'Superseded',
};

const CRITICALITY_MIX: readonly (readonly [Criticality, number])[] = [
  ['MissionCritical', 14],
  ['BusinessCritical', 31],
  ['Important', 43],
  ['Low', 12],
];

const DISPOSITION_BY_HEALTH: Record<TechnicalHealth, readonly (readonly [Disposition, number])[]> =
  {
    Good: [
      ['Retain', 88],
      ['Investigate', 12],
    ],
    Degraded: [
      ['Retain', 45],
      ['Modernize', 40],
      ['Investigate', 15],
    ],
    AtRisk: [
      ['Modernize', 45],
      ['Consolidate', 25],
      ['Replace', 20],
      ['Investigate', 10],
    ],
    Poor: [
      ['Replace', 45],
      ['Retire', 25],
      ['Consolidate', 20],
      ['Modernize', 10],
    ],
    Unknown: [['Investigate', 100]],
  };

function businessValueFor(criticality: Criticality): Application['businessValue'] {
  const high = criticality === 'MissionCritical';
  const mid = criticality === 'BusinessCritical';
  const rating = (strong: boolean, medium: boolean): 'High' | 'Medium' | 'Low' =>
    strong ? 'High' : medium ? 'Medium' : 'Low';
  return [
    { dimension: 'Revenue Impact', rating: rating(high, mid) },
    { dimension: 'Operational Impact', rating: rating(high, mid) },
    { dimension: 'Customer Impact', rating: rating(high || mid, criticality === 'Important') },
    { dimension: 'Compliance Impact', rating: rating(high, mid) },
  ];
}

export function buildApplications(
  asOf: string,
  rng: Rng,
  count: number = DEFAULT_APPLICATION_COUNT,
): Application[] {
  const appRng = rng.fork('applications');
  const stamp = provenanceFactory(asOf, rng.fork('applications-provenance'));
  const applications: Application[] = [];

  const finish = (
    input: Omit<Application, 'id' | 'technicalHealth' | 'lifecycleStatusLabel'>,
  ): Application => ({
    ...input,
    id: idFor('Application', input.businessCode),
    technicalHealth: technicalHealthFromScore(input.technicalHealthScore),
    lifecycleStatusLabel: LIFECYCLE_LABELS[input.lifecycle],
  });

  for (const seed of CURATED_APPS) {
    const runCost = millions(seed.annualRunCostMillions);
    const lifecycle = seed.lifecycle ?? 'Active';
    applications.push(
      finish({
        businessCode: seed.code,
        name: seed.name,
        domain: seed.domain,
        businessCapability: seed.capability,
        ownerPersonCode: at(
          ['PPL-0059', 'PPL-0060', 'PPL-0057', 'PPL-0053', 'PPL-0054'],
          applications.length % 5,
        ),
        technologyOwnerPersonCode: at(
          ['PPL-0005', 'PPL-0053', 'PPL-0054'],
          applications.length % 3,
        ),
        criticality: seed.criticality,
        technicalHealthScore: seed.technicalHealthScore,
        lifecycle,
        isDuplicateCandidate: (seed.duplicateOfCodes ?? []).length > 0,
        duplicateOfCodes: seed.duplicateOfCodes ?? [],
        duplicateOverlapScore: seed.duplicateOfCodes ? round(appRng.float(0.58, 0.86), 2) : null,
        disposition: seed.disposition ?? null,
        dispositionRationale: seed.disposition
          ? dispositionRationale(seed.disposition, seed.technicalHealthScore)
          : null,
        dispositionConfidence: seed.disposition ? round(appRng.float(0.62, 0.92), 2) : null,
        annualRunCostCents: runCost,
        // TCO carries the multi-year capital and exit cost the run figure hides.
        totalCostOfOwnershipCents: Math.round(runCost * appRng.float(1.35, 1.9)),
        technicalDebtCents: Math.round(
          runCost * ((100 - seed.technicalHealthScore) / 100) * appRng.float(0.8, 1.6),
        ),
        version: seed.version,
        vendorCode: seed.vendorCode,
        hostingEnvironment: seed.hosting,
        architecture: seed.architecture,
        technologyStack: seed.stack,
        database: seed.database,
        dataSensitivity: seed.dataSensitivity,
        securityScore: clamp(
          Math.round(seed.technicalHealthScore + appRng.around(-4, 9, -18, 14)),
          22,
          98,
        ),
        availability30d: round(
          clamp(0.999 - (100 - seed.technicalHealthScore) / 22000, 0.972, 0.99998),
          5,
        ),
        integrationCount: seed.integrationCount,
        lifecycleSince: seed.lifecycleSince,
        nextReview: addDays(asOf, appRng.int(30, 420)),
        businessValue: businessValueFor(seed.criticality),
        isCurated: true,
        provenance: stamp(SOURCE_SYSTEMS.cmdb, `CMDB-${seed.code}`, {
          meanAgeDays: 5,
          meanConfidence: 0.93,
        }),
      }),
    );
  }

  // ── Generated tail ─────────────────────────────────────────────────────────
  for (let i = applications.length; i < count; i += 1) {
    const code = `APP-${pad(i + 1, 4)}`;
    const domain = at(DOMAINS, appRng.int(0, DOMAINS.length - 1));
    const capabilities = CAPABILITY_BY_DOMAIN[domain] ?? ['General'];
    const capability = at(capabilities, appRng.int(0, capabilities.length - 1));
    const criticality = appRng.weighted(CRITICALITY_MIX);
    const lifecycle = appRng.weighted(LIFECYCLE_MIX);
    const healthScore = clamp(
      Math.round(appRng.around(criticality === 'MissionCritical' ? 68 : 64, 18, 18, 96)),
      18,
      96,
    );
    const health = technicalHealthFromScore(healthScore);
    // ~16% of the estate is flagged as overlapping something else. Kept high on
    // purpose: consolidation is the headline saving in the target scenario, and
    // the number has to be defensible on the applications screen.
    const isDuplicate = appRng.chance(0.16);
    const runCostCents = dollars(
      Math.round(
        appRng.around(
          criticality === 'MissionCritical'
            ? 1_900_000
            : criticality === 'BusinessCritical'
              ? 620_000
              : criticality === 'Important'
                ? 190_000
                : 64_000,
          criticality === 'MissionCritical' ? 1_100_000 : 260_000,
          18_000,
          9_400_000,
        ),
      ),
    );
    const disposition = appRng.weighted(DISPOSITION_BY_HEALTH[health]);

    applications.push(
      finish({
        businessCode: code,
        name: `${at(PREFIXES, appRng.int(0, PREFIXES.length - 1))}${at(SUFFIXES, appRng.int(0, SUFFIXES.length - 1))} ${pad(appRng.int(1, 89), 2)}`,
        domain,
        businessCapability: capability,
        ownerPersonCode: at(
          ['PPL-0059', 'PPL-0060', 'PPL-0057', 'PPL-0053', 'PPL-0054', 'PPL-0005'],
          i % 6,
        ),
        technologyOwnerPersonCode: at(['PPL-0005', 'PPL-0053', 'PPL-0054'], i % 3),
        criticality,
        technicalHealthScore: healthScore,
        lifecycle,
        isDuplicateCandidate: isDuplicate,
        duplicateOfCodes: isDuplicate ? [`APP-${pad(appRng.int(1, Math.max(1, i)), 4)}`] : [],
        duplicateOverlapScore: isDuplicate ? round(appRng.float(0.42, 0.91), 2) : null,
        disposition,
        dispositionRationale: dispositionRationale(disposition, healthScore),
        dispositionConfidence: round(appRng.float(0.38, 0.9), 2),
        annualRunCostCents: runCostCents,
        totalCostOfOwnershipCents: Math.round(runCostCents * appRng.float(1.3, 2.1)),
        technicalDebtCents: Math.round(
          runCostCents * ((100 - healthScore) / 100) * appRng.float(0.7, 1.7),
        ),
        version: `${appRng.int(1, 24)}.${appRng.int(0, 9)}`,
        vendorCode: appRng.chance(0.42)
          ? at(
              ['SUP-MERIDIAN', 'SUP-NORTHWIND', 'SUP-CALDER', 'SUP-BRIGHTPATH', 'SUP-VANTAGE'],
              appRng.int(0, 4),
            )
          : null,
        hostingEnvironment: at(HOSTING, appRng.int(0, HOSTING.length - 1)),
        architecture: at(ARCHITECTURES, appRng.int(0, ARCHITECTURES.length - 1)),
        technologyStack: at(STACKS, appRng.int(0, STACKS.length - 1)),
        database: at(DATABASES, appRng.int(0, DATABASES.length - 1)),
        dataSensitivity: appRng.weighted([
          ['High', 24],
          ['Medium', 44],
          ['Low', 32],
        ] as const),
        securityScore: clamp(Math.round(healthScore + appRng.around(-3, 12, -22, 16)), 18, 99),
        availability30d: round(clamp(0.999 - (100 - healthScore) / 18000, 0.958, 0.99998), 5),
        integrationCount: appRng.int(0, 96),
        lifecycleSince: `${appRng.int(1996, 2025)}-${pad(appRng.int(1, 12), 2)}-${pad(appRng.int(1, 28), 2)}`,
        nextReview: addDays(asOf, appRng.int(-90, 460)),
        businessValue: businessValueFor(criticality),
        isCurated: false,
        provenance: stamp(
          SOURCE_SYSTEMS.cmdb,
          `CMDB-${code}`,
          // Roughly one application in twelve has not been touched by the CMDB
          // feed in months. Those rows are what make the freshness badge and the
          // "stale" page state worth building.
          i % 12 === 0
            ? { meanAgeDays: 96, meanConfidence: 0.44 }
            : { meanAgeDays: 8, meanConfidence: 0.84 },
        ),
      }),
    );
  }

  return applications;
}

function dispositionRationale(disposition: Disposition, healthScore: number): string {
  switch (disposition) {
    case 'Retain':
      return `Technical health ${healthScore}/100 with no overlapping capability identified; no action beyond routine lifecycle management.`;
    case 'Modernize':
      return `Technical health ${healthScore}/100 with rising change cost; modernise to reduce technical debt and improve maintainability.`;
    case 'Consolidate':
      return 'Significant capability overlap with an adjacent system; consolidate to remove duplicate run cost and integration surface.';
    case 'Replace':
      return `Technical health ${healthScore}/100 with a constrained skills market and no viable upgrade path; replace.`;
    case 'Retire':
      return 'Low business value, declining usage and a viable alternative already in production; retire and decommission.';
    case 'Investigate':
    default:
      return 'Insufficient evidence to recommend a disposition. Ownership and usage need confirming before a decision is taken.';
  }
}
